import { useVideoDataContext } from "../contexts/VideoDataContext";
import { getInitDuration, getIsVertical } from "../utils";
import { useAutoplayContext } from "../contexts/AutoplayContext";
import { usePromoContext } from "../contexts/PromoContext";
import { useOrcaAdsContext } from "../contexts/OrcaAdsContext";
import { useResetVideoState } from "./useResetVideoState";
import useVideoControls from "./useVideoControls";
import { useHlsContext } from "../contexts/HlsContext";
import { useVideoElementRefContext } from "../contexts/VideoElementRefContext";
import { useStickyVideoContext } from "../contexts/StickyVideoContext";
import { useCallback, useRef, useState } from "react";
import { StickyPlayerEventLabel, VideoEvent } from "../../lib/analytics/utils";
import { useVideoAnalyticsContext } from "../contexts/VideoAnalyticsContext";
import { AdState } from "../../globals";
import { useAdsElementsRefContext } from "../contexts/AdsElementsRefContext";
import useFullscreen from "./useFullscreen";
import { usePlayerSize } from "./usePlayerSize";
import { useOrcaInitialized } from "./useOrcaInitialized";

/**
 * Custom hooks to handle interdependent video functionalities and states.
 * This hook combines various functionalities such as video controls, autoplay,
 * promo management, ad state management, and fullscreen handling.
 * It provides a unified interface to manage the video player state and interactions.
 * @param {Object} config
 */
// TODO - try and split this into smaller hooks if possible
export const useCommonHooks = (config, optionalHandlers = {}) => {
  const { videoData } = useVideoDataContext();
  const { updateAutoplayState, autoplayState } = useAutoplayContext();
  const { promoState, setPromoState } = usePromoContext();
  const { adState, setAdState } = useOrcaAdsContext();
  const { removeHLS } = useHlsContext();
  const videoElementRef = useVideoElementRefContext();
  const { isOpenSticky, updateStickyContext } = useStickyVideoContext();
  const { sendAnalyticsEvent } = useVideoAnalyticsContext();
  const { adControlsRef } = useAdsElementsRefContext();

  // Any custom handlers from particular verisons of the player
  const { toggleSubsHandler, toggleMuteHandler } = optionalHandlers;

  const [renderControls, setRenderControls] = useState(false);
  const [isNativeIOSPlayer, setIsNativeIOSPlayer] = useState(false);

  const initDuration = getInitDuration(videoData);

  // Setup refs
  const playerWrapperRef = useRef(null);
  const promoElement = useRef(null);
  const fullScreenWrapper = useRef();

  const playerSize = usePlayerSize(playerWrapperRef);

  const { initializeNewVideo } = useOrcaInitialized(config);

  const { debug, forceShowFullScreenBtn, isCarousel } = config;

  const {
    playerState,
    onPlayButton,
    play,
    pause,
    reset,
    toggleMute,
    handleVolumeChange,
    setShowSubtitles,
    handlePlayEvent,
    resetControlsState,
    handleLivePreview,
    setInitialMuteState,
    autoplay
  } = useVideoControls({
    config,
    isNativeIOSPlayer,
    adState,
    updateAutoplayState,
    toggleSubsHandler,
    toggleMuteHandler
  });

  const closeStickyPlayer = useCallback(() => {
    sendAnalyticsEvent(VideoEvent.STICKY_PLAYER_CLOSE, {
      label: StickyPlayerEventLabel.CLICK_CLOSE
    });
    adState === AdState.PLAYING ? adControlsRef.current?.pauseAd() : pause();
    updateStickyContext({ playingId: null, openId: null });
  }, [adState, pause, sendAnalyticsEvent, updateStickyContext]);

  const isVertical = getIsVertical(videoData);

  const {
    isFullscreen,
    setFullscreen: setIsFullscreen,
    showFullscreenBtn,
    closeFullscreen
  } = useFullscreen(
    debug,
    fullScreenWrapper,
    isCarousel,
    isVertical,
    setIsNativeIOSPlayer,
    playerState.showSubtitles,
    setShowSubtitles,
    setRenderControls,
    forceShowFullScreenBtn
  );

  const resetVideoState = useResetVideoState({
    autoplayState,
    resetControlsState,
    setAdState,
    updateAutoplayState,
    adState,
    removeHLS,
    videoElementRef,
    closeStickyPlayer,
    isOpenSticky,
    setIsFullscreen,
    setRenderControls,
    setPromoState,
    isFullscreen
  });

  return {
    initDuration,
    initializeNewVideo,
    promoState,
    setPromoState,
    resetVideoState,
    isFullscreen,
    isNativeIOSPlayer,
    setIsFullscreen,
    reset,
    playerState,
    playerSize,
    renderControls,
    setRenderControls,
    playerWrapperRef,
    promoElement,
    fullScreenWrapper,
    onPlayButton,
    play,
    pause,
    toggleMute,
    handleVolumeChange,
    setShowSubtitles,
    handlePlayEvent,
    resetControlsState,
    handleLivePreview,
    setInitialMuteState,
    autoplay,
    showFullscreenBtn,
    closeFullscreen,
    closeStickyPlayer
  };
};
